import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Activity, LayoutGrid, Shield, Users } from 'lucide-react';
import { useAuthContext } from '../../components/auth/AuthProvider';
import { getLogoUrl } from '../../shared/utils/imageUtils';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, login } = useAuthContext();

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate('/home');
    }
  }, [isAuthenticated, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (isAuthenticated) {
    return null; // Will redirect to /home
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-blue-100 rounded-lg flex items-center justify-center mx-auto mb-4">
              <img
                src={getLogoUrl()}
                alt="DoIT Logo"
                className="w-16 h-16 object-contain"
              />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Welcome to DoIT
            </h1>
            <p className="text-lg text-gray-600">
              Sign in to access your business process management platform
            </p>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center mb-6">
              <p className="text-sm text-gray-600">
                Click below to authenticate with your enterprise credentials
              </p>
            </div>

            <button
              onClick={login}
              className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-lg font-medium"
            >
              <LogIn size={20} />
              Sign In with Enterprise ID
            </button>

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                Secure authentication powered by Keycloak
              </p>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-sm text-gray-500">
              Need help? Contact your system administrator
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Features Preview */}
      <div className="flex-1 bg-blue-600 text-white p-8 flex items-center justify-center">
        <div className="max-w-lg">
          <h2 className="text-3xl font-bold mb-6">
            Business Process Management Platform
          </h2>
          <p className="text-lg mb-8 text-blue-100">
            Streamline your workflows with our integrated BPMN studio, AI-powered automation, 
            and comprehensive process analytics.
          </p>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Activity size={20} />
              </div>
              <div>
                <h3 className="font-semibold">BPMN Studio</h3>
                <p className="text-blue-100 text-sm">Design and model business processes</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <LayoutGrid size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Process Engine</h3>
                <p className="text-blue-100 text-sm">Execute workflows with intelligent automation</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Shield size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Enterprise Security</h3>
                <p className="text-blue-100 text-sm">Secure access with enterprise-grade authentication</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Users size={20} />
              </div>
              <div>
                <h3 className="font-semibold">Team Collaboration</h3>
                <p className="text-blue-100 text-sm">Manage users, roles, and permissions</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
